import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard } from "../lib/ui";

export default function KnowledgeOverview() {
  const [sources, setSources] = useState(null);
  const [chunks, setChunks] = useState(null);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/knowledge/sources"),
          api.get("/knowledge/chunks?limit=6"),
        ]);
        if (!alive) return;
        setSources(s.data?.sources || []);
        setChunks(c.data?.chunks || []);
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  function handleSearch(e) {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/brand-os/retrieval?q=${encodeURIComponent(query.trim())}`);
  }

  return (
    <Shell>
      <div className="bo-page" data-testid="knowledge-overview-page">
        <header className="bo-page-header">
          <div>
            <div className="bo-eyebrow">KNOWLEDGE</div>
            <h1 data-testid="knowledge-overview-title">What Brand OS knows</h1>
            <p className="bo-page-lede">
              Sources ingested, chunks embedded, and retrieval you can search against.
            </p>
          </div>
        </header>

        {err && <ErrorBanner error={err} testId="knowledge-overview-error" />}

        <div className="bo-grid-2" data-testid="knowledge-overview-cards">
          <section className="bo-card" data-testid="knowledge-sources-card">
            <header className="bo-card-header">
              <h3>Sources</h3>
              <Link to="/brand-os/retrieval" className="bo-link-sm">Explore →</Link>
            </header>
            {!sources ? (
              <LoadingCard />
            ) : sources.length === 0 ? (
              <EmptyState testId="knowledge-no-sources" title="No sources yet" description="Upload documents to build the knowledge base." />
            ) : (
              <>
                <div className="bo-metric-row">
                  <div className="bo-metric">
                    <div className="bo-metric-label">Sources</div>
                    <div className="bo-metric-value">{sources.length}</div>
                  </div>
                  <div className="bo-metric">
                    <div className="bo-metric-label">Chunks</div>
                    <div className="bo-metric-value">{chunks ? chunks.length : "–"}</div>
                  </div>
                </div>
                <ul className="bo-list-tight">
                  {sources.slice(0, 5).map((s) => (
                    <li key={s.id}>
                      <div>
                        <div className="bo-list-title">{s.title || s.name || "(untitled)"}</div>
                        {s.source_kind && <div className="bo-list-sub">{s.source_kind}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="bo-card" data-testid="knowledge-search-card">
            <header className="bo-card-header">
              <h3>Semantic search</h3>
              <Link to="/brand-os/retrieval" className="bo-link-sm">Full search →</Link>
            </header>
            <p style={{ color: "var(--bo-muted)", fontSize: 13, marginTop: 0 }}>
              Ask a question in natural language. Brand OS returns the most relevant chunks with authority + provenance.
            </p>
            <form onSubmit={handleSearch} data-testid="knowledge-search-form">
              <input
                className="bo-input"
                data-testid="knowledge-search-input"
                placeholder="e.g. what is our data policy?"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button
                type="submit"
                className="bo-btn bo-btn-primary"
                data-testid="knowledge-search-submit"
                style={{ marginTop: 10 }}
                disabled={!query.trim()}
              >
                Search
              </button>
            </form>
          </section>
        </div>
      </div>
    </Shell>
  );
}
